import { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MdCancel, MdZoomIn, MdZoomOut } from 'react-icons/md';
import 'react-phone-number-input/style.css';
// import { toast } from 'react-toastify';
import { FixedCropper, ImageRestriction } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import PrimaryBtn from 'components/btns/PrimaryBtn';

const aspectRatios = [
  {
    width: 16,
    height: 9,
  },
  {
    width: 4,
    height: 15,
  },
  {
    width: 8,
    height: 1,
  },
];

const imageNames = ['mobile_crop', 'vertical_crop', 'horizontal_crop'];

export default function AdImagesCropper({
  ModalHeader,
  isViewModal,
  setIsViewModal,
  setIsViewPreviousModal,
  setAdImages,
  isApiLoading,
  cropImage,
}) {
  const [image] = useState(cropImage?.dataURL);
  const [index] = useState(cropImage?.index);
  const [fileType] = useState(cropImage?.file?.type?.split('/')[1]);

  const cropperRef = useRef(null);
  const cancelButtonRef = useRef(null);

  const handleCroppedImage = () => {
    const croppedDataURL = cropperRef.current.getCanvas()?.toDataURL();

    if (croppedDataURL) {
      const file = dataURLtoFile(
        croppedDataURL,
        `${imageNames[index]}.${fileType}`,
        cropImage?.file?.type
      );

      setAdImages(cropImage?.index, file, croppedDataURL);
      setIsViewModal(false);
    }
  };

  function dataURLtoFile(dataURL, fileName, fileType) {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([new Blob([u8arr], { type: mime })], fileName, {
      type: fileType,
    });
  }

  const handleZoomIn = () => {
    if (cropperRef.current) {
      cropperRef.current.zoomImage(1.2); // You can adjust the zoom factor as needed
    }
  };

  const handleZoomOut = () => {
    if (cropperRef.current) {
      cropperRef.current.zoomImage(0.8); // You can adjust the zoom factor as needed
    }
  };

  return (
    <Transition.Root show={isViewModal} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-10'
        initialFocus={cancelButtonRef}
        onClose={() => setIsViewModal(true)}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center py-4 text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='w-full relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full md:max-w-4xl max-w-2xl'>
                <div className='bg-white p-0'>
                  <div className='ez__PlanTripHead w-full bg-ezGreen p-4 md:px-6 flex items-center justify-between'>
                    <p className='text-white'>{ModalHeader}</p>
                    <button
                      type='button'
                      onClick={() => {
                        setIsViewModal(false);
                      }}
                      className='bg-white text-ezGreen rounded-full block w-max'
                    >
                      <MdCancel className='w-8 h-8' />
                    </button>
                  </div>
                  <div className='flex items-center justify-center mt-3 -mb-2 text-ezGray'>
                    <p>
                      Crop the image to the required aspect ratio{' '}
                      <strong className='text-black'>
                        {aspectRatios[index].width} :{' '}
                        {aspectRatios[index].height}
                      </strong>
                    </p>
                  </div>
                  <div className='relative w-full h-full'>
                    <div className='h-96 w-full flex items-center justify-center p-5'>
                      <FixedCropper
                        ref={cropperRef}
                        src={image}
                        className={`px-10 pt-10 pb-20 hover:cursor-move ${
                          cropImage?.aspectRatio === '8:1' ? 'h-96' : ''
                        }`}
                        stencilSize={{
                          width: `${aspectRatios[index].width * 100}`,
                          height: `${aspectRatios[index].height * 100}`,
                        }}
                        stencilProps={{
                          grid: true,
                          handlers: false,
                          movable: false,
                          resizable: false,
                        }}
                        imageRestriction={ImageRestriction.stencil}
                      />
                    </div>
                    <div className='absolute bottom-10 flex justify-center w-full space-x-2'>
                      {' '}
                      {/* Updated: Positioned absolute at the bottom */}
                      <button onClick={handleZoomIn}>
                        <div className='rounded-full border hover:bg-white p-2'>
                          <MdZoomIn className='w-6 h-6 text-white hover:text-green-900' />
                        </div>
                      </button>
                      <button onClick={handleZoomOut}>
                        <div className='rounded-full border hover:bg-white p-2'>
                          <MdZoomOut className='w-6 h-6 text-white hover:text-green-900' />
                        </div>
                      </button>
                    </div>
                  </div>
                  <div className='w-full flex align-middle justify-center'>
                    <PrimaryBtn
                      handleEvent={handleCroppedImage}
                      btnType='button'
                      isApiLoading={isApiLoading}
                      text='Crop & Save'
                      classNames='mx-5 mb-5 w-32'
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
